import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";

import { useCart } from "../context/CartContext";

import {
  FaTruck,
  FaInfoCircle,
} from "react-icons/fa";



/* ============================================================
   PRODUCT DETAILS
   ============================================================ */

function ProductDetails() {

  const { id } = useParams();

  const {
    addToCart,
    cart,
  } = useCart();


  /* ============================================================
     SAFE CART
     ============================================================ */

  const safeCart = Array.isArray(cart) ? cart : [];


  /* ============================================================
     STATE
     ============================================================ */

  const [product, setProduct] = useState(null);

  const [images, setImages] = useState([]);

  const [activeImage, setActiveImage] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);


  /* ============================================================
     API CONFIGURATION

     IMPORTANT:

     REACT_APP_API_URL should be:

     Development:
     https://apimyshop.myidealtd.co.uk/api

     Production:
     https://apimyshop.myidealtd.co.uk/api

     We deliberately remove /api when creating image URLs.

     API:
     https://apimyshop.myidealtd.co.uk/api

     IMAGE BASE:
     https://apimyshop.myidealtd.co.uk
     ============================================================ */

  const API_URL = useMemo(() => {

    const envUrl =
      process.env.REACT_APP_API_URL;

    const url =
      envUrl ||
      "https://apimyshop.myidealtd.co.uk/api";

    return url
      .trim()
      .replace(/\/+$/, "");

  }, []);


  /* ============================================================
     IMAGE BASE URL

     Example:

     API_URL:
     https://apimyshop.myidealtd.co.uk/api

     IMAGE_BASE_URL:
     https://apimyshop.myidealtd.co.uk
     ============================================================ */

  const IMAGE_BASE_URL = useMemo(() => {

    return API_URL
      .replace(/\/api\/?$/i, "")
      .replace(/\/+$/, "");

  }, [API_URL]);


  /* ============================================================
     DEFAULT IMAGE

     Put this file in:

     public/images/no-image.png
     ============================================================ */

  const DEFAULT_IMAGE =
    "/images/no-image.png";


  /* ============================================================
     DEBUG API CONFIGURATION

     Remove these console logs later if required.
     ============================================================ */

  useEffect(() => {

    console.log(
      "=========================================="
    );

    console.log(
      "ProductDetails API CONFIG"
    );

    console.log(
      "API_URL:",
      API_URL
    );

    console.log(
      "IMAGE_BASE_URL:",
      IMAGE_BASE_URL
    );

    console.log(
      "Product ID:",
      id
    );

    console.log(
      "=========================================="
    );

  }, [API_URL, IMAGE_BASE_URL, id]);


  /* ============================================================
     IMAGE URL HELPER

     Supports:

     1. Full URL

        https://example.com/image.jpg

     2. Object

        {
          main: "/uploads/products/test.jpg",
          original: "...",
          thumbnail: "...",
          cart: "..."
        }

     3. /uploads/products/test.jpg

     4. uploads/products/test.jpg

     5. products/test.jpg

     6. test.jpg

     7. /api/uploads/products/test.jpg

     ============================================================ */

  const getImageUrl = (img) => {

    /* ----------------------------------------------------------
       No image
       ---------------------------------------------------------- */

    if (!img) {
      return DEFAULT_IMAGE;
    }


    /* ----------------------------------------------------------
       IMAGE OBJECT

       Prefer:

       main
       original
       thumbnail
       cart
       url
       path
       filename
       ---------------------------------------------------------- */

    if (typeof img === "object") {

      img =
        img.main ||
        img.original ||
        img.thumbnail ||
        img.cart ||
        img.url ||
        img.path ||
        img.filename ||
        "";
    }


    /* ----------------------------------------------------------
       Still no image
       ---------------------------------------------------------- */

    if (!img) {
      return DEFAULT_IMAGE;
    }


    /* ----------------------------------------------------------
       Convert to string
       ---------------------------------------------------------- */

    img = String(img).trim();


    if (!img) {
      return DEFAULT_IMAGE;
    }


    /* ----------------------------------------------------------
       Full HTTP / HTTPS URL

       Example:

       https://apimyshop.myidealtd.co.uk/uploads/a.jpg

       Return unchanged.
       ---------------------------------------------------------- */

    if (
      img.startsWith("http://") ||
      img.startsWith("https://")
    ) {

      return img;
    }


    /* ----------------------------------------------------------
       Remove leading slash

       /uploads/a.jpg

       becomes:

       uploads/a.jpg
       ---------------------------------------------------------- */

    img = img.replace(/^\/+/, "");


    /* ----------------------------------------------------------
       Remove accidental API prefix

       api/uploads/a.jpg

       becomes:

       uploads/a.jpg
       ---------------------------------------------------------- */

    img = img.replace(
      /^api\/+/i,
      ""
    );


    /* ----------------------------------------------------------
       Remove accidental api prefix again if path contains:

       /api/uploads
       ---------------------------------------------------------- */

    img = img.replace(
      /^\/?api\/+/i,
      ""
    );


    /* ----------------------------------------------------------
       Already starts with uploads/

       uploads/products/a.jpg

       becomes:

       https://apimyshop.myidealtd.co.uk/uploads/products/a.jpg
       ---------------------------------------------------------- */

    if (
      img.toLowerCase().startsWith("uploads/")
    ) {

      return `${IMAGE_BASE_URL}/${img}`;
    }


    /* ----------------------------------------------------------
       Starts with products/

       products/a.jpg

       becomes:

       https://apimyshop.myidealtd.co.uk/uploads/products/a.jpg
       ---------------------------------------------------------- */

    if (
      img.toLowerCase().startsWith("products/")
    ) {

      return `${IMAGE_BASE_URL}/uploads/${img}`;
    }


    /* ----------------------------------------------------------
       Starts with images/

       images/a.jpg

       We assume it is directly under backend root.

       ---------------------------------------------------------- */

    if (
      img.toLowerCase().startsWith("images/")
    ) {

      return `${IMAGE_BASE_URL}/${img}`;
    }


    /* ----------------------------------------------------------
       Plain filename

       product.jpg

       becomes:

       https://apimyshop.myidealtd.co.uk/uploads/product.jpg
       ---------------------------------------------------------- */

    return `${IMAGE_BASE_URL}/uploads/${img}`;
  };


  /* ============================================================
     NORMALISE PRODUCT IMAGE DATA

     Converts all possible backend image formats into
     a clean array.

     Example result:

     [
       {
         source: {...},
         url: "https://apimyshop.myidealtd.co.uk/uploads/a.jpg"
       }
     ]

     But we store the original values because the active
     image state needs to work with the original object.
     ============================================================ */

  const extractProductImages = (data) => {

    const result = [];


    /* ----------------------------------------------------------
       images array
       ---------------------------------------------------------- */

    if (Array.isArray(data?.images)) {

      data.images.forEach((img) => {

        if (!img) {
          return;
        }

        /* Object */

        if (typeof img === "object") {

          const candidate =
            img.main ||
            img.original ||
            img.thumbnail ||
            img.cart ||
            img.url ||
            img.path ||
            img.filename;

          if (candidate) {
            result.push(img);
          }

          return;
        }


        /* String */

        if (
          typeof img === "string" &&
          img.trim()
        ) {

          result.push(img);
        }

      });
    }


    /* ----------------------------------------------------------
       Legacy image field
       ---------------------------------------------------------- */

    if (
      result.length === 0 &&
      data?.image
    ) {

      if (
        Array.isArray(data.image)
      ) {

        data.image.forEach((img) => {

          if (img) {
            result.push(img);
          }

        });

      } else {

        result.push(
          data.image
        );
      }
    }


    /* ----------------------------------------------------------
       Other possible image fields

       Some older product records may contain:

       imageUrl
       imageURL
       mainImage
       thumbnail
       ---------------------------------------------------------- */

    if (
      result.length === 0 &&
      data?.imageUrl
    ) {

      result.push(
        data.imageUrl
      );
    }


    if (
      result.length === 0 &&
      data?.imageURL
    ) {

      result.push(
        data.imageURL
      );
    }


    if (
      result.length === 0 &&
      data?.mainImage
    ) {

      result.push(
        data.mainImage
      );
    }


    if (
      result.length === 0 &&
      data?.thumbnail
    ) {

      result.push(
        data.thumbnail
      );
    }


    /* ----------------------------------------------------------
       Remove duplicates
       ---------------------------------------------------------- */

    const unique = [];

    const seen = new Set();

    result.forEach((img) => {

      const url =
        getImageUrl(img);

      if (
        url &&
        !seen.has(url)
      ) {

        seen.add(url);

        unique.push(img);
      }

    });


    return unique;
  };


  /* ============================================================
     GET PRIMARY IMAGE

     Prefer "main" image when available.
     ============================================================ */

  const getPrimaryImage = (imageList) => {

    if (
      !Array.isArray(imageList) ||
      imageList.length === 0
    ) {

      return null;
    }


    /* ----------------------------------------------------------
       Look for object containing main
       ---------------------------------------------------------- */

    const mainImage =
      imageList.find(
        (img) =>
          typeof img === "object" &&
          img?.main
      );


    if (mainImage) {
      return mainImage;
    }


    /* ----------------------------------------------------------
       Otherwise first image
       ---------------------------------------------------------- */

    return imageList[0];
  };


  /* ============================================================
     FETCH PRODUCT
     ============================================================ */

  useEffect(() => {

    let mounted = true;


    const fetchProduct = async () => {

      try {

        setLoading(true);

        setError("");


        /* ------------------------------------------------------
           Safety check
           ------------------------------------------------------ */

        if (!id) {

          throw new Error(
            "Product ID is missing."
          );
        }


        /* ------------------------------------------------------
           Build endpoint safely

           API_URL:
           https://apimyshop.myidealtd.co.uk/api

           Result:
           https://apimyshop.myidealtd.co.uk/api/products/123
           ------------------------------------------------------ */

        const endpoint =
          `${API_URL}/products/${encodeURIComponent(id)}`;


        console.log(
          "ProductDetails REQUEST:",
          endpoint
        );


        /* ------------------------------------------------------
           Fetch product
           ------------------------------------------------------ */

        const res =
          await axios.get(
            endpoint,
            {
              withCredentials: true,
            }
          );


        console.log(
          "ProductDetails RESPONSE:",
          res.data
        );


        /* ------------------------------------------------------
           Backend may return:

           {
             product: {...}
           }

           OR:

           {...}
           ------------------------------------------------------ */

        const data =
          res.data?.product ||
          res.data?.data?.product ||
          res.data?.data ||
          res.data;


        /* ------------------------------------------------------
           Validate
           ------------------------------------------------------ */

        if (
          !data ||
          !(
            data._id ||
            data.id
          )
        ) {

          throw new Error(
            "Invalid product response."
          );
        }


        if (!mounted) {
          return;
        }


        /* ------------------------------------------------------
           Extract images
           ------------------------------------------------------ */

        const productImages =
          extractProductImages(data);


        console.log(
          "ProductDetails RAW IMAGES:",
          data.images
        );


        console.log(
          "ProductDetails NORMALISED IMAGES:",
          productImages
        );


        /* ------------------------------------------------------
           Debug final image URLs
           ------------------------------------------------------ */

        productImages.forEach(
          (img, index) => {

            console.log(
              `Product image ${index + 1}:`,
              getImageUrl(img)
            );

          }
        );


        /* ------------------------------------------------------
           Save product
           ------------------------------------------------------ */

        setProduct(data);


        /* ------------------------------------------------------
           Save images
           ------------------------------------------------------ */

        setImages(
          productImages
        );


        /* ------------------------------------------------------
           Select primary image
           ------------------------------------------------------ */

        const primaryImage =
          getPrimaryImage(
            productImages
          );


        setActiveImage(
          primaryImage
        );


      } catch (err) {

        console.error(
          "=========================================="
        );

        console.error(
          "ProductDetails fetch error:"
        );

        console.error(
          err
        );

        console.error(
          "Response:",
          err.response?.data
        );

        console.error(
          "Status:",
          err.response?.status
        );

        console.error(
          "=========================================="
        );


        if (!mounted) {
          return;
        }


        /* ------------------------------------------------------
           Better error messages
           ------------------------------------------------------ */

        let message =
          "Unable to load product.";


        if (
          err.response?.status === 404
        ) {

          message =
            "Product not found. Please check the product ID and API endpoint.";
        }
        else if (
          err.response?.status === 401
        ) {

          message =
            "You are not authorised to view this product.";
        }
        else if (
          err.response?.status >= 500
        ) {

          message =
            "The server encountered an error while loading this product.";
        }
        else if (
          err.message
        ) {

          message =
            err.message;
        }


        setError(
          err.response?.data?.message ||
          message
        );


      } finally {

        if (mounted) {

          setLoading(false);
        }
      }
    };


    fetchProduct();


    return () => {

      mounted = false;
    };


  }, [API_URL, id]);


  /* ============================================================
     CART CHECK
     ============================================================ */

  const isInCart =
    safeCart.some(
      (item) =>
        String(
          item?._id ||
          item?.id
        ) ===
        String(
          product?._id ||
          product?.id
        )
    );


  /* ============================================================
     ADD TO CART
     ============================================================ */

  const handleAddToCart = () => {

    if (!product) {
      return;
    }


    if (isInCart) {

      setShowModal(true);

      return;
    }


    addToCart(product);
  };


  /* ============================================================
     IMAGE ERROR HANDLER
     ============================================================ */

  const handleImageError = (e) => {

    const failedUrl =
      e.currentTarget.src;


    console.error(
      "=========================================="
    );

    console.error(
      "PRODUCT IMAGE FAILED"
    );

    console.error(
      "Failed URL:",
      failedUrl
    );

    console.error(
      "Product:",
      product?.name
    );

    console.error(
      "=========================================="
    );


    /* ----------------------------------------------------------
       Prevent infinite fallback loop
       ---------------------------------------------------------- */

    if (
      failedUrl.includes(
        DEFAULT_IMAGE
      )
    ) {

      return;
    }


    e.currentTarget.onerror = null;

    e.currentTarget.src =
      DEFAULT_IMAGE;
  };


  /* ============================================================
     IMAGE CLICK
     ============================================================ */

  const handleThumbnailClick = (
    image
  ) => {

    setActiveImage(
      image
    );
  };


  /* ============================================================
     LOADING
     ============================================================ */

  if (loading) {

    return (

      <div className="loading-screen">

        <Spinner
          animation="border"
          role="status"
        />

        <span className="visually-hidden">
          Loading...
        </span>

      </div>
    );
  }


  /* ============================================================
     ERROR
     ============================================================ */

  if (
    error ||
    !product
  ) {

    return (

      <Container className="py-5">

        <Alert variant="danger">

          {error ||
            "Product not found."}

        </Alert>


        <Button
          as={Link}
          to="/products"
          variant="dark"
        >

          Back to Products

        </Button>

      </Container>
    );
  }


  /* ============================================================
     DELIVERY DATE
     ============================================================ */

  const deliveryDate =
    new Date();

  deliveryDate.setDate(
    deliveryDate.getDate() + 3
  );


  /* ============================================================
     CURRENT IMAGE URL
     ============================================================ */

  const currentImageUrl =
    getImageUrl(
      activeImage
    );


  /* ============================================================
     RENDER
     ============================================================ */

  return (

    <>

      <Container
        className="product-page py-5"
      >


        {/* =====================================================
            BREADCRUMB
        ===================================================== */}

        <div className="breadcrumb">

          <Link to="/">
            Home
          </Link>

          <span>
            /
          </span>

          <Link to="/products">
            Products
          </Link>

          <span>
            /
          </span>

          <span>
            {product.name}
          </span>

        </div>


        {/* =====================================================
            MAIN PRODUCT AREA
        ===================================================== */}

        <Row
          className="g-5 align-items-start"
        >


          {/* =================================================
              IMAGE SECTION
          ================================================= */}

          <Col
            md={6}
            className="product-image-column"
          >

            <Card
              className="image-card"
            >

              <div
                className="main-image-wrapper"
              >

                <img
                  src={currentImageUrl}
                  alt={
                    product.name ||
                    "Product"
                  }
                  className="main-image"
                  onError={
                    handleImageError
                  }
                  loading="eager"
                />

              </div>

            </Card>


            {/* =================================================
                IMAGE URL DEBUG

                Remove this section after confirming images work.
            ================================================= */}

            {process.env.NODE_ENV === "development" && (

              <div
                className="image-debug"
              >

                <small>
                  Image URL:
                </small>

                <div>
                  {currentImageUrl}
                </div>

              </div>

            )}


            {/* =================================================
                THUMBNAILS
            ================================================= */}

            {images.length > 0 && (

              <div
                className="thumbnail-row"
              >

                {images.map(
                  (img, idx) => {

                    const thumbnailUrl =
                      getImageUrl(img);


                    const activeUrl =
                      getImageUrl(
                        activeImage
                      );


                    const isActive =
                      thumbnailUrl ===
                      activeUrl;


                    return (

                      <div
                        key={`${thumbnailUrl}-${idx}`}
                        className={
                          `thumb ${
                            isActive
                              ? "active"
                              : ""
                          }`
                        }
                        onClick={() =>
                          handleThumbnailClick(
                            img
                          )
                        }
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {

                          if (
                            e.key ===
                            "Enter"
                          ) {

                            handleThumbnailClick(
                              img
                            );
                          }

                        }}
                      >

                        <img
                          src={thumbnailUrl}
                          alt={
                            `${product.name} ${
                              idx + 1
                            }`
                          }
                          loading="lazy"
                          onError={
                            handleImageError
                          }
                        />

                      </div>
                    );
                  }
                )}

              </div>
            )}


            {/* =================================================
                NO IMAGE MESSAGE
            ================================================= */}

            {images.length === 0 && (

              <div
                className="no-image-message"
              >

                No product images are available.

              </div>

            )}

          </Col>


          {/* =================================================
              PRODUCT INFORMATION
          ================================================= */}

          <Col md={6}>

            <div
              className="info-card"
            >


              {/* CATEGORY */}

              {product.category && (

                <span
                  className="category"
                >

                  {typeof product.category ===
                    "object"
                    ? product.category.name
                    : product.category}

                </span>

              )}


              {/* PRODUCT NAME */}

              <h1 className="title">

                {product.name}

              </h1>


              {/* BRAND */}

              {product.brand && (

                <div className="brand">

                  {typeof product.brand ===
                    "object"
                    ? product.brand.name
                    : product.brand}

                </div>

              )}


              {/* PRICE */}

              <div className="price">

                £
                {Number(
                  product.price || 0
                ).toFixed(2)}

              </div>


              {/* STOCK */}

              {typeof product.stock !==
                "undefined" && (

                <div
                  className={
                    product.stock > 0
                      ? "stock available"
                      : "stock unavailable"
                  }
                >

                  {product.stock > 0
                    ? `${product.stock} in stock`
                    : "Out of stock"}

                </div>

              )}


              {/* DELIVERY */}

              <div
                className="delivery"
              >

                <FaTruck />

                <span>

                  Delivered by{" "}

                  {deliveryDate.toDateString()}

                </span>

              </div>


              {/* DESCRIPTION */}

              <p className="desc">

                {product.description ||
                  "No description available."}

              </p>


              {/* ADD TO CART */}

              <Button
                className="buy-btn"
                disabled={
                  isInCart ||
                  product.stock === 0
                }
                onClick={
                  handleAddToCart
                }
              >

                {product.stock === 0
                  ? "Out of Stock"
                  : isInCart
                  ? "Already in Cart"
                  : "Add to Cart"}

              </Button>


              {/* SECURITY */}

              <div
                className="secure"
              >

                🔒 Secure checkout
                {" • "}
                Fast delivery
                {" • "}
                Easy returns

              </div>

            </div>

          </Col>

        </Row>

      </Container>


      {/* =======================================================
          ALREADY IN CART MODAL
      ======================================================= */}

      <Modal
        show={showModal}
        onHide={() =>
          setShowModal(false)
        }
        centered
      >

        <Modal.Header
          closeButton
        >

          <Modal.Title>

            <FaInfoCircle
              className="text-warning me-2"
            />

            Already in Cart

          </Modal.Title>

        </Modal.Header>


        <Modal.Body
          className="text-center"
        >

          This product is already
          in your cart.

        </Modal.Body>


        <Modal.Footer>

          <Button
            onClick={() =>
              setShowModal(false)
            }
          >

            OK

          </Button>

        </Modal.Footer>

      </Modal>


      {/* =======================================================
          STYLES
      ======================================================= */}

      <style>{`

        /* ======================================================
           LOADING
        ====================================================== */

        .loading-screen {

          min-height: 100vh;

          display: flex;

          justify-content: center;

          align-items: center;

          gap: 12px;

          background: #f7f8fc;

        }


        /* ======================================================
           PAGE
        ====================================================== */

        .product-page {

          background: #f7f8fc;

          min-height: 100vh;

        }


        /* ======================================================
           BREADCRUMB
        ====================================================== */

        .breadcrumb {

          font-size: 13px;

          margin-bottom: 25px;

          color: #777;

        }


        .breadcrumb a {

          text-decoration: none;

          color: #666;

        }


        .breadcrumb a:hover {

          color: #111;

        }


        .breadcrumb span {

          margin: 0 8px;

          color: #bbb;

        }


        /* ======================================================
           IMAGE COLUMN
        ====================================================== */

        .product-image-column {

          min-width: 0;

        }


        /* ======================================================
           IMAGE CARD
        ====================================================== */

        .image-card {

          border: none;

          border-radius: 18px;

          background: #fff;

          box-shadow:
            0 10px 30px
            rgba(0, 0, 0, 0.06);

          overflow: hidden;

        }


        /* ======================================================
           MAIN IMAGE WRAPPER
        ====================================================== */

        .main-image-wrapper {

          padding: 30px;

          text-align: center;

          min-height: 450px;

          display: flex;

          justify-content: center;

          align-items: center;

          background: #fff;

        }


        /* ======================================================
           MAIN IMAGE

           Important:

           width: 100%
           height: 420px
           object-fit: contain

           means the entire product remains visible.
        ====================================================== */

        .main-image {

          display: block;

          width: 100%;

          height: 420px;

          max-height: 420px;

          object-fit: contain;

          transition:
            transform 0.3s ease;

        }


        .image-card:hover
        .main-image {

          transform:
            scale(1.04);

        }


        /* ======================================================
           THUMBNAILS
        ====================================================== */

        .thumbnail-row {

          display: flex;

          gap: 10px;

          justify-content: flex-start;

          margin-top: 15px;

          flex-wrap: wrap;

        }


        .thumb {

          width: 70px;

          height: 70px;

          padding: 6px;

          border-radius: 12px;

          border:
            1px solid #e5e5e5;

          cursor: pointer;

          background: #fff;

          transition:
            all 0.2s ease;

        }


        .thumb:hover {

          transform:
            translateY(-2px);

          border-color:
            #999;

        }


        .thumb img {

          display: block;

          width: 100%;

          height: 100%;

          object-fit: contain;

        }


        .thumb.active {

          border:
            2px solid #111;

        }


        /* ======================================================
           IMAGE DEBUG
        ====================================================== */

        .image-debug {

          margin-top: 10px;

          padding: 8px 10px;

          border-radius: 8px;

          background: #f1f1f1;

          color: #666;

          font-size: 11px;

          word-break: break-all;

        }


        .image-debug small {

          display: block;

          font-weight: 700;

          margin-bottom: 3px;

        }


        /* ======================================================
           NO IMAGE
        ====================================================== */

        .no-image-message {

          text-align: center;

          color: #888;

          font-size: 13px;

          margin-top: 12px;

        }


        /* ======================================================
           PRODUCT INFORMATION
        ====================================================== */

        .info-card {

          background: #fff;

          padding: 35px;

          border-radius: 18px;

          box-shadow:
            0 10px 30px
            rgba(0, 0, 0, 0.06);

          position: sticky;

          top: 20px;

        }


        /* ======================================================
           CATEGORY
        ====================================================== */

        .category {

          display: inline-block;

          font-size: 12px;

          padding: 6px 12px;

          background: #eef3ff;

          color: #0d6efd;

          border-radius: 999px;

          font-weight: 600;

        }


        /* ======================================================
           BRAND
        ====================================================== */

        .brand {

          margin-top: 10px;

          font-size: 13px;

          color: #777;

          font-weight: 600;

        }


        /* ======================================================
           TITLE
        ====================================================== */

        .title {

          margin-top: 12px;

          font-weight: 700;

          font-size: 26px;

          line-height: 1.3;

        }


        /* ======================================================
           PRICE
        ====================================================== */

        .price {

          font-size: 28px;

          font-weight: 700;

          color: #198754;

          margin: 15px 0;

        }


        /* ======================================================
           STOCK
        ====================================================== */

        .stock {

          font-size: 13px;

          font-weight: 600;

          margin-bottom: 15px;

        }


        .stock.available {

          color: #198754;

        }


        .stock.unavailable {

          color: #dc3545;

        }


        /* ======================================================
           DELIVERY
        ====================================================== */

        .delivery {

          display: flex;

          align-items: center;

          gap: 8px;

          color: #555;

          margin-bottom: 15px;

        }


        /* ======================================================
           DESCRIPTION
        ====================================================== */

        .desc {

          color: #666;

          line-height: 1.7;

          font-size: 14px;

          white-space: pre-line;

        }


        /* ======================================================
           BUY BUTTON
        ====================================================== */

        .buy-btn {

          width: 100%;

          background: #111;

          border: none;

          border-radius: 14px;

          padding: 12px;

          font-weight: 600;

          margin-top: 15px;

        }


        .buy-btn:hover {

          background: #222;

        }


        .buy-btn:disabled {

          background: #777;

          opacity: 0.8;

        }


        /* ======================================================
           SECURITY
        ====================================================== */

        .secure {

          margin-top: 12px;

          font-size: 12px;

          text-align: center;

          color: #888;

        }


        /* ======================================================
           MOBILE
        ====================================================== */

        @media (max-width: 767px) {

          .product-page {

            padding-top: 25px !important;

          }


          .main-image-wrapper {

            min-height: 320px;

            padding: 20px;

          }


          .main-image {

            height: 300px;

            max-height: 300px;

          }


          .info-card {

            position: static;

            padding: 25px;

          }


          .title {

            font-size: 23px;

          }


          .price {

            font-size: 25px;

          }


          .thumb {

            width: 60px;

            height: 60px;

          }

        }

      `}</style>

    </>
  );
}


export default ProductDetails;